import { Button, Divider, Drawer, Stack, FormLabel } from "@mui/material";
import { useMemo } from "react";

import { groupByFirstLetter } from "components/Forms/components/fields/select/utils";

import { usePclContext } from "../../context/PclContextProvider";

import {
  buildUniqueStrings,
  generateProtectionProfileOptions,
  generateTechTypeOptions,
} from "./FilterUtils";
import PclStatusOptions from "./PclStatusOptions";

import "components/Products/Products.css";

interface PclFilterProps {
  show: boolean;
}

export default function PclFilter({ show }: PclFilterProps) {
  const { products, form } = usePclContext();

  const options = useMemo(() => {
    if (!products) {
      return {
        techTypes: [] as string[],
        vendors: [] as string[],
        protectionProfiles: [] as string[],
        certifyingCountries: [] as string[],
      };
    }

    const techTypes = generateTechTypeOptions(products);

    const vendors = buildUniqueStrings(products, "vendor_id_name");
    const certifyingCountries = buildUniqueStrings(
      products,
      "submitting_country_id_name",
    );

    const protectionProfiles = generateProtectionProfileOptions(products);

    return {
      techTypes,
      vendors,
      protectionProfiles,
      certifyingCountries,
    };
  }, [products]);

  return (
    <Drawer
      open={show}
      variant="persistent"
      sx={{
        position: "sticky",
        top: 0,
        height: "fit-content",
        width: show ? "250px" : 0,
        ".MuiDrawer-paperAnchorLeft": {
          position: "static",
          top: 0,
          marginRight: "1em",
          marginBottom: "1em",
          paddingTop: "0",
          marginTop: "1em",
          flex: 0,
          paddingRight: "1em",
          overflowY: "visible",
        },
      }}
    >
      <form>
        <form.AppForm>
          <form.AppField name="search">
            {(field) => <field.SearchBar captureSearch={true} />}
          </form.AppField>

          <Stack gap={1.25} marginTop={3}>
            <FormLabel>Filter Options</FormLabel>
            <form.AppField name="techType">
              {(field) => (
                <field.AutocompleteSelect
                  options={options.techTypes}
                  label="Technology Type"
                  groupBy={groupByFirstLetter}
                />
              )}
            </form.AppField>

            <form.AppField name="vendor">
              {(field) => (
                <field.AutocompleteSelect
                  options={options.vendors}
                  label="Vendor"
                  groupBy={groupByFirstLetter}
                />
              )}
            </form.AppField>

            <form.AppField name="protectionProfile">
              {(field) => (
                <field.AutocompleteSelect
                  options={options.protectionProfiles}
                  label="Protection Profile"
                />
              )}
            </form.AppField>

            <form.AppField name="country">
              {(field) => (
                <field.AutocompleteSelect
                  options={options.certifyingCountries}
                  label="Certifying Country"
                />
              )}
            </form.AppField>
          </Stack>

          <form.Field name="status">
            {(field) => <PclStatusOptions field={field} />}
          </form.Field>

          <Divider
            sx={{ opacity: 1, borderColor: "rgba(0,0,0,.15)", margin: 2 }}
          />

          <Button
            fullWidth
            onClick={() => {
              form.reset();
              form.setFieldValue("status", () => "");
            }}
            sx={{
              backgroundColor: "#212529",
              color: "white",
              "&:hover": {
                background: "#424649",
              },
            }}
          >
            Clear Filters
          </Button>
        </form.AppForm>
      </form>
    </Drawer>
  );
}
